import { baseUrl } from '../../utils/axios'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { IDepartmentsGetAll, IDepartmentsSlice } from 'types/departments.interface'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { INameId } from 'constants/globalTypes'
import { IDepratmnetsGetSingleResponse } from 'types/api/departmentsApi.interface'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'

const initialState: IDepartmentsSlice = {
  isLoading: false,
  departments: [],
  singleDepartment: null,
  totalCount: null,
  totalPages: null,
}

export const getDepartments = createAsyncThunk(
  'departments/getDepartments',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: IDepartmentsGetAll = await baseUrl.get('departments', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addDepartment = createAsyncThunk(
  'departments/addDepartment',
  async (name: string, thunkAPI) => {
    try {
      const resp = await baseUrl.post('/departments', { name })
      thunkAPI.dispatch(getDepartments(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const archiveDepartment = createAsyncThunk(
  'departments/archiveDepartment',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch('departments/' + id + '/archive', thunkAPI)
      await thunkAPI.dispatch(getDepartments(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleDepartment = createAsyncThunk(
  'departments/getSingleDepartment',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IDepratmnetsGetSingleResponse>> =
        await baseUrl.get('departments/' + id)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateDepartment = createAsyncThunk(
  'departments/updateDepartment',
  async ({ name, id }: { name: string; id: number }, thunkAPI) => {
    try {
      const resp = await baseUrl.patch('departments/' + id, { name }, thunkAPI)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const departmentsSlice = createSlice({
  name: 'departments',
  initialState,
  reducers: {
    clearDepartment: (state) => {
      state.singleDepartment = null
    },
    clearDepartmentsList: (state) => {
      state.departments = []
      state.totalCount = 0
      state.totalPages = 0
    },
  },
  extraReducers: {
    [getDepartments.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDepartments.fulfilled.type]: (
      state: IDepartmentsSlice,
      { payload }: { payload: IPaginatedResponse<INameId> },
    ) => {
      state.isLoading = false
      state.departments = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getDepartments.rejected.type]: (state) => {
      state.isLoading = false
    },
    [addDepartment.pending.type]: (state) => {
      state.isLoading = true
    },
    [addDepartment.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [addDepartment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleDepartment.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleDepartment.fulfilled.type]: (
      state: IDepartmentsSlice,
      { payload }: { payload: IDepratmnetsGetSingleResponse },
    ) => {
      state.isLoading = false
      const copy: any = { ...payload }
      copy.supervisors = payload.supervisors.map((s) => ({
        id: s.id,
        name: s.firstName + ' ' + s.lastName,
      }))
      state.singleDepartment = copy
    },
    [getSingleDepartment.rejected.type]: (state) => {
      state.isLoading = false
    },
    [updateDepartment.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateDepartment.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      const foundIndex = state.departments.findIndex((item) => item.id === payload.id)
      state.departments[foundIndex] = payload
    },
    [updateDepartment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [archiveDepartment.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Department archived')
    },
    [archiveDepartment.pending.type]: (state) => {
      state.isLoading = true
    },
    [archiveDepartment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export default departmentsSlice.reducer
export const { clearDepartment, clearDepartmentsList } = departmentsSlice.actions
